import React, { useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Event as EventIcon,
  Flight as TripIcon,
  Business as OrgIcon,
  CardMembership as SubscriptionIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUser } from '../../contexts/UserContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { formatDistanceToNow } from 'date-fns';

export interface EntityChannel {
   id: string;
   type: 'event' | 'trip' | 'organization' | 'subscription';
   entityId: string;
   entityName: string;
   createdAt: Date;
   lastUpdated?: Date;
   lastMessageAt?: Date;
   unreadCount?: number;
   metadata?: any;
   organizationId?: string | null;
   isPersonal?: boolean;
   avatarUrl?: string; // Cover photo URL for events and trips
}

interface EntityChannelListProps {
   onChannelSelect: (channel: EntityChannel) => void;
   activeChannelId?: string;
}

const EntityChannelList: React.FC<EntityChannelListProps> = ({
   onChannelSelect,
   activeChannelId,
}) => {
   const { currentUser } = useUser();
   const { currentOrganization } = useOrganization();
   const [channels, setChannels] = useState<EntityChannel[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);

   // Determine if we're in personal mode (no organization selected)
   const isPersonalMode = !currentOrganization;

   // Helper function to safely format destination/location strings
   const formatDestination = (destination: any) => {
      if (!destination) return null;

      // If destination is a string, return it
      if (typeof destination === 'string') {
         return destination;
      }

      // If destination is an object, try to extract name, address, or other string fields
      if (typeof destination === 'object') {
         return (
            destination.name ||
            destination.address ||
            destination.city ||
            destination.label ||
            'Unknown'
         );
      }

      return 'Unknown';
   };

   useEffect(() => {
      if (!currentUser) {
         setChannels([]);
         setLoading(false);
         return;
      }

      setLoading(true);

      // Determine the appropriate query based on context (personal or organization mode)
      let channelsQuery;

      if (isPersonalMode) {
         // Personal mode - fetch chatRooms where user is a member and it's marked as personal
         // or related to trips/events that are personal
         channelsQuery = query(
            collection(db, 'chatRooms'),
            where('members', 'array-contains', currentUser.uid),
            where('roomType', 'in', ['trip', 'event']), // Only trip and event rooms
            orderBy('lastActivity', 'desc')
         );
      } else if (currentOrganization) {
         // Organization mode - fetch chatRooms associated with the current organization
         channelsQuery = query(
            collection(db, 'chatRooms'),
            where('organizationId', '==', currentOrganization.id),
            where('roomType', 'in', ['trip', 'event', 'organization']),
            orderBy('lastActivity', 'desc')
         );
      } else {
         // Fallback query - fetch trip and event rooms where user is a member
         channelsQuery = query(
            collection(db, 'chatRooms'),
            where('members', 'array-contains', currentUser.uid),
            where('roomType', 'in', ['trip', 'event']),
            orderBy('lastActivity', 'desc')
         );
      }

      // Set up real-time listener
      const unsubscribe = onSnapshot(
         channelsQuery,
         (snapshot) => {
            const channelList: EntityChannel[] = [];

            snapshot.docs.forEach((doc) => {
               const data = doc.data();

               // Map chatRoom data to EntityChannel format
               const roomType = data.roomType || 'event'; // Default to event if not specified
               const entityName =
                  data.name ||
                  data.entityName ||
                  `${roomType.charAt(0).toUpperCase() + roomType.slice(1)} Chat`;

               channelList.push({
                  id: doc.id,
                  type: roomType as 'event' | 'trip' | 'organization' | 'subscription',
                  entityId: data.entityId || data.tripId || data.eventId || doc.id,
                  entityName: entityName,
                  createdAt: data.createdAt?.toDate() || new Date(),
                  lastUpdated: data.lastActivity?.toDate(),
                  lastMessageAt: data.lastMessage?.timestamp?.toDate(),
                  unreadCount: data.unreadCount || 0,
                  metadata: data.metadata || {
                     startDate: data.startDate,
                     endDate: data.endDate,
                     destination: data.destination || data.primaryDestination?.name,
                     location: data.location,
                  },
                  organizationId: data.organizationId || null,
                  isPersonal: data.isPersonal || !data.organizationId,
                  avatarUrl: data.avatarUrl,
               });
            });

            setChannels(channelList);
            setLoading(false);
         },
         (err) => {
            console.error('Error fetching entity channels from chatRooms:', err);
            setError('Failed to load channels');
            setLoading(false);
         }
      );

      return () => unsubscribe();
   }, [currentUser, isPersonalMode, currentOrganization]);

   const getChannelIcon = (channelType: string) => {
      switch (channelType) {
         case 'event':
            return <EventIcon color='primary' />;
         case 'trip':
            return <TripIcon color='secondary' />;
         case 'organization':
            return <OrgIcon color='success' />;
         case 'subscription':
            return <SubscriptionIcon color='info' />;
         default:
            return <EventIcon />;
      }
   };

   // Helper function to get avatar content for a channel
   const getChannelAvatar = (channel: EntityChannel) => {
      // Use cover photo if available for events and trips
      if (channel.avatarUrl && (channel.type === 'event' || channel.type === 'trip')) {
         return (
            <Avatar
               src={channel.avatarUrl}
               sx={{
                  width: 40,
                  height: 40,
                  border: '2px solid',
                  borderColor: channel.type === 'event' ? 'primary.main' : 'secondary.main',
               }}
            >
               {/* Fallback to first letter of name if image fails to load */}
               {channel.entityName?.[0]?.toUpperCase() || 'C'}
            </Avatar>
         );
      }

      // Fallback to icon-based avatar
      return <Avatar>{getChannelIcon(channel.type)}</Avatar>;
   };

   const getChannelSecondaryText = (channel: EntityChannel) => {
      switch (channel.type) {
         case 'event':
            return channel.metadata?.startDate
               ? `Event Date: ${new Date(channel.metadata.startDate.seconds * 1000).toLocaleDateString()}`
               : 'Event';
         case 'trip':
            return channel.metadata?.destination
               ? `Trip to ${formatDestination(channel.metadata.destination)}`
               : 'Trip';
         case 'organization':
            return 'Organization';
         case 'subscription':
            return 'Subscription';
         default:
            return '';
      }
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
         </Box>
      );
   }

   if (error) {
      return (
         <Box sx={{ p: 2 }}>
            <Typography color='error'>{error}</Typography>
         </Box>
      );
   }

   if (channels.length === 0) {
      return (
         <Box sx={{ p: 2 }}>
            <Typography color='text.secondary'>
               {isPersonalMode
                  ? 'No personal trip or event channels found.'
                  : `No channels found for ${currentOrganization?.name || 'this organization'}.`}
            </Typography>
         </Box>
      );
   }

   return (
      <List disablePadding>
         {channels.map((channel) => (
            <React.Fragment key={channel.id}>
               <ListItem
                  button
                  selected={channel.id === activeChannelId}
                  onClick={() => onChannelSelect(channel)}
                  sx={{
                     backgroundColor:
                        channel.id === activeChannelId ? 'action.selected' : 'transparent',
                     '&:hover': {
                        backgroundColor: 'action.hover',
                     },
                  }}
               >
                  <ListItemAvatar>
                     <Badge
                        color='error'
                        badgeContent={channel.unreadCount}
                        invisible={!channel.unreadCount}
                     >
                        {getChannelAvatar(channel)}
                     </Badge>
                  </ListItemAvatar>
                  <ListItemText
                     primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                           <Typography variant='body1' noWrap sx={{ flexGrow: 1 }}>
                              {channel.entityName}
                           </Typography>
                           <Typography variant='caption' color='text.secondary'>
                              {channel.lastMessageAt
                                 ? formatDistanceToNow(channel.lastMessageAt, { addSuffix: true })
                                 : ''}
                           </Typography>
                        </Box>
                     }
                     secondary={getChannelSecondaryText(channel)}
                  />
               </ListItem>
               <Divider component='li' />
            </React.Fragment>
         ))}
      </List>
   );
};

export default EntityChannelList;
